/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Modal } from 'semantic-ui-react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName, selectCurrentUserNotificationEmail, selectCurrentUserNumberUnclearedNotifications, selectCurrentUserNumberUnreadNotifications, numberUnClearedNotificationsChanged, numberUnreadNotificationsChanged, userLoggedOut, selectDisplayTheTermsOfAgreement, changeWebsiteView, selectWebsiteView, changeSseConnectionId } from "../slices/currentUser/currentUserSlice";
import { selectGlobalLoading } from "../slices/globalLoading/globalLoadingSlice";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { selectEnvironment } from "../slices/environment/environmentSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectToolName } from "../slices/footer/footerSlice";
import { selectFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";
import { getUserNotificationNumbers } from "../slices/notifications/notificationsFunctionsAPI";
import getFhirJson from "../functions/getFhirJsonAPI";
import NotificationsView from "./NotificationsView";
import AlertModal from '../components/AlertModal';
import { ConfirmModal } from "../components/ConfirmModal";
import server_config from '../ServerConfiguration';
import { ReactComponent as BellEmoji } from '../assets/bellemoji.svg';
import "../App.css";

const fevir_websocket_address = server_config["fevir_websocket_address"];
//const fevir_website_address = server_config["fevir_website_address"];

const delay = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

const onLogoutClicked = async (dispatch) => {
    await firebase.auth().signOut().then(async () => {
        console.log("Signing out...");
        dispatch(userLoggedOut());
    }).catch((error) => { console.log(error); });
}

const updateResourceInCollection = async (foi, apiToken, dispatch) => {
    //console.log("referenced " + foi + " changed");
    let fhirJson = await getFhirJson(foi, null, apiToken, false);
    dispatch(resourceUpdated({ "id": foi, "resourceJson": fhirJson, "revisedResourceJson": fhirJson, "sourceOfChanges": "from-server" }));
}

const useOnClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
}

const ResourceRedirectToast = ({ foi, message }) => {
    //Makes the toast clickable to get to the resource in a new tab
    if (foi) {
        return <div style={{ cursor: "pointer", width: "720px !important" }}
            onClick={() => { window.open("/" + foi, '_blank').focus(); }}>
            <a target="_blank">{message}</a>
        </div>
    } else {
        return <div><span>{message}</span></div>
    }
}

const NotificationModal = ({ notificationHover, notificationLeave,
    notificationModalOpen, setNotificationModalOpen }) => {
    const modalRef = useRef();

    const modalClose = () => {
        setNotificationModalOpen(false);
    }

    useOnClickOutside(modalRef, () => modalClose());

    return <Modal
        className="notificationsModal"
        style={{ right: "10px", top: "30px", width: "900px", maxWidth: "70%", margin: "16px" }}
        onMouseEnter={notificationHover}
        onMouseLeave={notificationLeave}
        open={notificationModalOpen}
        dimmer={false}
        centered={false}
        content={<div ref={modalRef} style={{ whiteSpace: "pre-wrap" }}>
            <div className="notificationsModalDiv scrolling content" style={{ padding: "10px 16px 10px 16px" }}>
                <NotificationsView />
            </div>
        </div>}
        closeOnEscape
        closeOnDimmerClick={false}
        onClose={modalClose}
    />
}

const reloadPage = () => { //Because for some reason passing window.location.reload didn't work
    window.location.reload();
}

const Navbar = ({ userLoadingState, setFirebaseModalState }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const path = location.pathname.substring(1);
    const siteIsLoading = useSelector(selectGlobalLoading);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const foi = useSelector(selectFoiFocus);
    const foiRef = useRef(foi);
    const resourceCollection = useSelector(state => state.resourceCollection.entities);
    const resourceCollectionRef = useRef({});
    const editMode = useSelector(selectEditMode);
    const editModeRef = useRef(editMode);
    const websiteView = useSelector(selectWebsiteView);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const isLoggedIn = (!!userId && userId !== "1");
    const toolName = useSelector(selectToolName);
    const isUnloadingRef = useRef(false);
    const environment = useSelector(selectEnvironment);
    const socketRef = useRef(null);
    const userIdRef = useRef(null);

    const connectionIdRef = useRef(null);

    const notificationemail = useSelector(selectCurrentUserNotificationEmail);
    const numberUnreadNotifications = useSelector(selectCurrentUserNumberUnreadNotifications);
    const numberUnclearedNotifications = useSelector(selectCurrentUserNumberUnclearedNotifications);
    const numberUnclearedNotificationsRef = useRef(null);
    //const { notificationBellRef } = useRef();
    const hoverNotificationBellRef = useRef(null);
    const hoverLeaveNotificationBellRef = useRef(null);
    const { addToast } = useToasts();
    const navigate = useNavigate();
    const displayTheTermsOfAgreement = useSelector(selectDisplayTheTermsOfAgreement);

    let subpageClass = "";
    if (path) {
        subpageClass = " navbarSubPage";
    }
    let onFevir = (environment === "FEvIR" || environment === "Development FEvIR");
    let fevirNavClass = "";
    if (onFevir) {
        fevirNavClass = " fevirNavbar";
    }

    const [bellShaking, setBellShaking] = useState(false);
    const [newNotificationBoolean, setNewNotificationBoolean] = useState(false); //TODO -- determine how to change it
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);
    const [confirmModalState, setConfirmModalState] = useState({ open: false });
    const [alertModalState, setAlertModalState] = useState({ open: false, alertHistory: [], header: "", content: "" });
    const [reconnectTrigger, setReconnectTrigger] = useState(0);

    useEffect(() => {
        if (displayTheTermsOfAgreement === true && path !== "termsofagreement" && path !== "termsofuse" && path !== "personaldataagreement") {
            navigate("/termsofagreement");
        }
    }, [displayTheTermsOfAgreement, path]);

    useEffect(() => {
        foiRef.current = foi;
    }, [foi]);

    useEffect(() => {
        resourceCollectionRef.current = resourceCollection;
    }, [resourceCollection]);

    useEffect(() => {
        editModeRef.current = editMode;
    }, [editMode]);

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        numberUnclearedNotificationsRef.current = numberUnclearedNotifications;
    }, [numberUnclearedNotifications]);

    const handleSocketData = async (data) => {
        //console.log("------------------------");
        //console.log(data);
        //I feel like this if block needs to be commented out? BUT I REALLY DON'T KNOW
        if (data.connectionId) {
            dispatch(changeSseConnectionId(data.connectionId));
        }
        if (data?.action === "update-notificationnumber-states-and-toast") {
            if (data.message) {
                addToast(<ResourceRedirectToast foi={data.target} message={data.message.substring(0, 500)} />, { appearance: 'info' });
            }
            setNewNotificationBoolean(true);
            if (apiToken) {
                const numbersResponse = await getUserNotificationNumbers(apiToken);
                if (numbersResponse.success) {
                    //let newUnread = (numberUnreadNotifications !== numbersResponse.numberunreadnotifications);
                    let newUncleared = (numberUnclearedNotificationsRef?.current !== numbersResponse.numberunclearednotifications);
                    dispatch(numberUnreadNotificationsChanged(numbersResponse.numberunreadnotifications));
                    //if (newUnread) { }
                    if (newUncleared) {
                        dispatch(numberUnClearedNotificationsChanged(numbersResponse.numberunclearednotifications));
                    }
                }
            }
        } else if (data?.action === "resource-updated-in-another-session") {
            console.log(connectionIdRef.current);
            if (data.senderConnectionId === connectionIdRef.current) { return; }
            if (data.foi == foiRef.current) { //This HAS to be a double equals and not triple because one is often a string vs a number
                let message;
                if (editModeRef.current === true) {
                    message = "Do you want to refresh this page so you'll be seeing the latest version of it, you'll lose your work? You can say 'No' and save your work and compare the version differences.";
                } else {
                    message = "Do you want to refresh this page so you'll be seeing the latest version of it?";
                }
                setConfirmModalState({
                    open: true, header: "This resource has been updated",
                    content: message,
                    runFunction: reloadPage, "functionParameters": null,
                    saveFunction: null, buttonTextOkay: "Yes", buttonTextCancel: "No",
                    buttonColorOkay: "green", buttonColorCancel: "red"
                });
            } else if (resourceCollectionRef.current?.[data.foi]) { //Means it's a resource that's referenced that was updated
                //(or a referenced resource)
                if (!editModeRef.current || data.userid === userIdRef.current) {
                    updateResourceInCollection(data.foi, apiToken, dispatch);
                } else {
                    //TODO for future use -- confirm modal if yes updateResourceInCollection
                    console.log(data.foi);
                }
            }
        } else if (data?.action === "limit-reached-for-ip") {
            console.log(data);
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Limit reached for this IP Address.",
                    "content": data.message
                };
            });
        } else if (data?.action === "hello-world") {
            //Placeholder action
        }
    }

    const establishConnection = async () => {
        const isLoggedInForWS = !!(userId && userId !== "1" && apiToken);

        //await delay(1);

        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        const foiFocus = foiRef.current || "false";
        let wsUrl = `${fevir_websocket_address}?foiFocus=${foiFocus}`;

        if (isLoggedInForWS) {
            wsUrl += `&apiToken=${encodeURIComponent(apiToken)}`;
        }

        const ws = new WebSocket(wsUrl);
        socketRef.current = ws;

        ws.onmessage = async (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.connectionId) {
                    connectionIdRef.current = data.connectionId;
                    dispatch(changeSseConnectionId(data.connectionId));
                }
                await handleSocketData(data);
            } catch (e) {
                console.error("Error parsing WebSocket data", e);
            }
        };

        ws.onerror = (error) => {
            if (!isUnloadingRef.current) {
                console.error('WebSocket failed:', error);
            }
        };

        ws.onclose = () => {
            socketRef.current = null;
            if (!isUnloadingRef.current) {
                setTimeout(() => {
                    //establishConnection();
                    setReconnectTrigger(prev => prev + 1); // new state variable
                }, 2000);
            }
        };

        return () => {
            if (socketRef.current) {
                ws.close();
                socketRef.current = null;
            }
        };
    };

    useEffect(() => {
        let cleanup;
        const run = async () => {
            cleanup = await establishConnection();
        };
        run();
        return () => {
            if (cleanup) { cleanup(); }
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, [apiToken, userId, reconnectTrigger]);

    useEffect(() => {
        if (newNotificationBoolean === true) {
            setBellShaking(true);
            const timer = setTimeout(() => {
                // Sets the bell to have a .shake class and changes it after it's done
                setBellShaking(false);
                if (newNotificationBoolean) { //Checks to make sure nothing else set it to false
                    setNewNotificationBoolean(false);
                }
            }, 500); // Matches the animation duration set in the CSS
            return () => clearTimeout(timer);
        }
    }, [newNotificationBoolean]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            isUnloadingRef.current = true;
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const notificationHover = () => {
        clearTimeout(hoverLeaveNotificationBellRef.current);
        hoverNotificationBellRef.current = setTimeout(() => { setNotificationModalOpen(true); }, 400);
    }

    const notificationLeave = () => {
        clearTimeout(hoverNotificationBellRef.current);
        hoverLeaveNotificationBellRef.current = setTimeout(() => { setNotificationModalOpen(false); }, 600);
    }

    return <div id="navbar" name="navbar" className={"navbar " + path + subpageClass + fevirNavClass}
        style={{ margin: "0px", width: "100%", position: "fixed" }}>
        <NotificationModal notificationHover={notificationHover} notificationLeave={notificationLeave}
            notificationModalOpen={notificationModalOpen} setNotificationModalOpen={setNotificationModalOpen} />
        {confirmModalState.open &&
            <ConfirmModal confirmModalState={confirmModalState} setConfirmModalState={setConfirmModalState} />}
        {alertModalState.open &&
            <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />}
        <table className="headerTable">
            <tbody>
                <tr>
                    <td className="navbarWebsiteLinkCell" style={{ whiteSpace: "nowrap", paddingLeft: ".2rem" }}>
                        {
                            environment === "Development FEvIR"
                                ?
                                <h2 style={{ display: "inline" }}>
                                    <a href="/" className={"unselectable"}>
                                        <span style={{ color: "red" }}>Development FEvIR</span>
                                    </a>
                                </h2>
                                :
                                environment === "FEvIR"
                                    ?
                                    <h2 style={{ display: "inline" }}>
                                        <a href="/" className={"unselectable"}>
                                            <span>FEvIR Platform</span>
                                        </a>
                                    </h2>
                                    :
                                    <>
                                        <Link to="" className="navbar-logo-container" style={{ display: "inline" }}>
                                            <img id="navbarlogo" className="navbar-logo navbar-logo-wide"
                                                alt="Computable Publishing logo" src="/png-01-wide-tiny.png" />
                                            <img className="navbar-logo navbar-logo-small"
                                                alt="Computable Publishing logo" src="/cplogo-192.png" />
                                        </Link>
                                    </>
                        }
                        {websiteView === "mobile" ?
                            <span className="desktopViewButton">&nbsp;&nbsp;<Button className="formButton" style={{ padding: "2px" }} onClick={() => { dispatch(changeWebsiteView("desktop")); }}>Desktop View</Button></span>
                            :
                            <span className="mobileViewButton">&nbsp;&nbsp;<Button className="formButton" style={{ padding: "2px" }} onClick={() => { dispatch(changeWebsiteView("mobile")); }}>Mobile View</Button></span>
                        }
                    </td>
                    <td style={{ width: "100%", textAlign: "center" }}>
                        {(!siteIsLoading && (!toolName || toolName === "FEvIR® Platform"))
                            &&
                            <div id="navbarPageLinks" className="right-justify navbar-page-links">
                                {
                                    environment === "Computable Publishing" &&
                                    <Link to={window.location.href.includes("computablepublishing.com") ? "https://fevir.net" : "/"}>FEvIR</Link>
                                }
                                <Link to="/mission" className={path === "mission" ? "selectedNavbarSubPage" : ""}>Making Science Computable</Link>
                                <Link to="/products"><span className={path === "products" ? "selectedNavbarSubPage" : ""}>Products</span></Link>
                                <Link to="/media" className={path === "media" ? "selectedNavbarSubPage" : ""}>Presentations</Link>
                                <Link to="/about" className={path === "about" ? "selectedNavbarSubPage" : ""}>Team</Link>
                            </div>
                        }
                    </td>
                    <td className="navbarUserInfoCell" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                        <div className="right-justify" style={{ margin: "6px 1px 0px 0px" }}>
                            <span style={{ paddingLeft: "20px" }}></span>
                            {isLoggedIn ?
                                <>
                                    {numberUnclearedNotifications > 0 &&
                                        <span>
                                            <span className={"unselectable " + (bellShaking ? "shake" : "noShake")}
                                                style={{
                                                    display: "inline-block", textAlign: "left",
                                                    width: "60px", marginRight: "-36px"
                                                }}>
                                                <span
                                                    className={"navigationNotificationBell" + (numberUnreadNotifications ? "" : " bellNoUnread")}>
                                                    <Link to={`/content/myresources`}
                                                        style={{ cursor: "pointer" }}
                                                        onMouseEnter={notificationHover}
                                                        onMouseLeave={notificationLeave}
                                                        onClick={notificationLeave}
                                                    >
                                                        <BellEmoji className="inline-emoji" />
                                                    </Link>
                                                </span>
                                                {numberUnreadNotifications > 0 &&
                                                    <span style={{
                                                        pointerEvents: "none", display: "inline-block",
                                                        position: "relative", textAlign: "center", overflow: "visible",
                                                        top: "-2px", left: "-43.50px", width: "60px"
                                                    }}>
                                                        <span style={{ zIndex: 3000, color: "#000000" }}>
                                                            {numberUnreadNotifications < 100 ?
                                                                numberUnreadNotifications
                                                                :
                                                                "99+"}
                                                        </span>
                                                        {/* TODO - the number unread is not displaying because it is behind the bell */}
                                                        {/* Currently 99+ doesn't look good, worse than 100 */}
                                                    </span>
                                                }
                                            </span>
                                        </span>
                                    }
                                    <span style={{ paddingLeft: "10px" }}></span>
                                    <Link to={'/editprofile'}>
                                        <span style={{ fontSize: onFevir ? '14px' : '18px', textDecoration: "underline" }}>
                                            {userName || "Edit User Profile"}
                                        </span>
                                    </Link>
                                    {(!notificationemail || notificationemail === "") &&
                                        <span style={{ paddingLeft: "4px" }}>
                                            <img style={{ height: '16px' }} src="/red-excl.png"
                                                alt="Incomplete profile, Please complete"
                                                title="Incomplete profile, Please complete" />
                                        </span>
                                    }
                                    <span style={{ paddingLeft: "6px" }}>
                                        <Button className="formButton logOutLogInButton"
                                            style={{ marginLeft: "4px", width: "83px" }}
                                            content="Log Out" compact
                                            onClick={() => { onLogoutClicked(dispatch) }} />
                                    </span>
                                </>
                                :
                                <>
                                    <span>
                                        <Button
                                            className="formButton logOutLogInButton"
                                            style={{ marginLeft: "4px" }}
                                            compact
                                            content={displayTheTermsOfAgreement ? "Please agree to the terms" : "Sign In / Create Account"}
                                            disabled={userLoadingState || displayTheTermsOfAgreement}
                                            onClick={() => {
                                                /*
                                                //This is for if we bring back Single Sign-On on computablepublishing.com
                                                if (window.location.href.includes("computablepublishing.com")) {
                                                    let fevirSsoAddress = fevir_website_address + "/noheader/sso?redirect=" + encodeURIComponent(window.location.origin + window.location.pathname);
                                                    window.location.assign(fevirSsoAddress);
                                                } else {
                                                    setFirebaseModalState(prevState => { return { ...prevState, 'open': true } }); //Regular fevir.net sign in where it opens up the modal
                                                }
                                                */
                                                setFirebaseModalState(prevState => { return { ...prevState, 'open': true } }); //Regular fevir.net sign in where it opens up the modal
                                            }} />
                                    </span>
                                </>}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>;
}

export default Navbar;